// Where the buying-interest stage coins sit, how strong each one is, and from
// which zoom it shows. Kept free of Mapbox so it can be unit tested.
//
// A coin is a density reading, not a cluster: its stage counts the interest
// inside one fixed-size circle on the ground, so zooming out never makes an
// area look busier than it is. Where two coins would overlap on screen, the
// stronger one is shown alone instead of the two being added together.

export type WeightedPoint = { lng: number; lat: number; w: number };
export type StageCoin = WeightedPoint & { minzoom: number };

// The fixed area one coin's stage is counted over: the same "± 500 m" a
// buyer's own circle interest covers. Kept small enough to still fit on screen
// at street zoom, where the coin hands over to the per-building coins.
export const COIN_AREA_RADIUS_M = 500;
const COIN_AREA_M2 = Math.PI * COIN_AREA_RADIUS_M ** 2;

// Interest weight within one coin area needed for stages 2..5:
// under 2 -> stage 1, 2-<5 -> stage 2, 5-<20 -> stage 3,
// 20-<100 -> stage 4, 100+ -> stage 5.
export const STAGE_THRESHOLDS = [2, 5, 20, 100];
export const STAGE_COUNT = 5;

// Display size [width, height] in CSS px of each stage's coin, stage 1 first.
// Must follow public/assets/markers/area-coin--<stage>--default.png, which are
// @2x exports: half their pixel size.
const COIN_SIZE_PX: [number, number][] = [
  [24, 24],
  [38.5, 24],
  [53.5, 24],
  [54.5, 34],
  [55.5, 48],
];
// Clear space kept between two coins shown at the same zoom.
const COIN_GAP_PX = 12;
// Mapbox GL renders the whole world 512 px wide at zoom 0.
const WORLD_PX_AT_Z0 = 512;

const M_PER_DEG_LAT = 111320;
const mPerDegLng = (lat: number) =>
  M_PER_DEG_LAT * Math.cos((lat * Math.PI) / 180);

/** Stage (1..5) for the interest weight inside one coin area. */
export function stageForWeight(weight: number): number {
  let stage = 1;
  for (let i = 0; i < STAGE_THRESHOLDS.length; i++) {
    if (weight >= STAGE_THRESHOLDS[i]) stage = i + 2;
  }
  return stage;
}

/**
 * How much of `count` interests spread evenly over `areaM2` falls inside one
 * coin area: all of it for an area no bigger than a coin's, a proportional
 * share for a bigger one — buyers who picked a whole city are not a crowd in
 * one of its neighbourhoods.
 */
export function areaWeight(count: number, areaM2: number): number {
  if (!Number.isFinite(areaM2) || areaM2 <= COIN_AREA_M2) return count;
  return (count * COIN_AREA_M2) / areaM2;
}

// Area of one lng/lat ring in m², projected flat around the ring's own latitude.
function ringAreaM2(ring: GeoJSON.Position[]): number {
  if (ring.length < 3) return 0;
  const [lng0, lat0] = ring[0];
  const kx = mPerDegLng(ring.reduce((sum, p) => sum + p[1], 0) / ring.length);
  let twice = 0;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = (ring[i][0] - lng0) * kx;
    const yi = (ring[i][1] - lat0) * M_PER_DEG_LAT;
    const xj = (ring[j][0] - lng0) * kx;
    const yj = (ring[j][1] - lat0) * M_PER_DEG_LAT;
    twice += xj * yi - xi * yj;
  }
  return Math.abs(twice) / 2;
}

/** Area in m² of a Polygon or MultiPolygon, holes excluded; 0 for anything else. */
export function polygonAreaM2(
  geometry: GeoJSON.Geometry | null | undefined,
): number {
  if (!geometry) return 0;
  const polygons =
    geometry.type === 'Polygon'
      ? [geometry.coordinates]
      : geometry.type === 'MultiPolygon'
        ? geometry.coordinates
        : [];
  let area = 0;
  for (const [outer = [], ...holes] of polygons) {
    area +=
      ringAreaM2(outer) -
      holes.reduce((sum, hole) => sum + ringAreaM2(hole), 0);
  }
  return Math.max(0, area);
}

function distanceM(a: WeightedPoint, b: WeightedPoint): number {
  const dx = (b.lng - a.lng) * mPerDegLng((a.lat + b.lat) / 2);
  const dy = (b.lat - a.lat) * M_PER_DEG_LAT;
  return Math.hypot(dx, dy);
}

// First index of ascending `sorted` whose value is >= `value`.
function lowerBound(sorted: number[], value: number): number {
  let lo = 0;
  let hi = sorted.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (sorted[mid] < value) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

/**
 * One site per coin area. The point with the most interest around it claims
 * every interest within COIN_AREA_RADIUS_M, then the densest point left claims
 * its own surroundings, and so on. Each interest counts towards exactly one
 * site, and a site sits at the weighted centre of the interest it claimed.
 */
export function coinSites(points: WeightedPoint[]): WeightedPoint[] {
  const pts = points.filter(
    (p) => Number.isFinite(p.lng) && Number.isFinite(p.lat) && p.w > 0,
  );
  const byLat = pts.map((_, i) => i).sort((a, b) => pts[a].lat - pts[b].lat);
  const lats = byLat.map((i) => pts[i].lat);
  const dLat = COIN_AREA_RADIUS_M / M_PER_DEG_LAT;
  const forEachWithin = (i: number, visit: (j: number) => void) => {
    const p = pts[i];
    for (
      let k = lowerBound(lats, p.lat - dLat);
      k < byLat.length && lats[k] <= p.lat + dLat;
      k++
    ) {
      const j = byLat[k];
      if (distanceM(p, pts[j]) <= COIN_AREA_RADIUS_M) visit(j);
    }
  };

  const density = pts.map((_, i) => {
    let sum = 0;
    forEachWithin(i, (j) => {
      sum += pts[j].w;
    });
    return sum;
  });
  const densestFirst = pts
    .map((_, i) => i)
    .sort(
      (a, b) =>
        density[b] - density[a] ||
        pts[b].w - pts[a].w ||
        pts[a].lat - pts[b].lat ||
        pts[a].lng - pts[b].lng,
    );

  const claimed = new Array<boolean>(pts.length).fill(false);
  const sites: WeightedPoint[] = [];
  for (const i of densestFirst) {
    if (claimed[i]) continue;
    let w = 0;
    let lng = 0;
    let lat = 0;
    forEachWithin(i, (j) => {
      if (claimed[j]) return;
      claimed[j] = true;
      w += pts[j].w;
      lng += pts[j].lng * pts[j].w;
      lat += pts[j].lat * pts[j].w;
    });
    sites.push({ lng: lng / w, lat: lat / w, w });
  }
  return sites;
}

const mercatorX = (lng: number) => (lng + 180) / 360;
const mercatorY = (lat: number) => {
  const sin = Math.sin(
    (Math.max(-85.0511, Math.min(85.0511, lat)) * Math.PI) / 180,
  );
  return 0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI);
};

/**
 * The lowest whole zoom each coin shows from, working out from `maxZoom` to 0.
 * At every zoom the coins still shown must fit on screen without touching;
 * where two would, the stronger stays and the other waits for a closer zoom.
 * The one that stays keeps its own stage — it is not the two added together.
 *
 * Coins are only ever hidden on the way out, so a coin shown at one zoom is
 * shown at every closer one, and each whole zoom level has one fixed set.
 */
export function assignMinZooms(
  sites: WeightedPoint[],
  maxZoom: number,
): StageCoin[] {
  const xs = sites.map((s) => mercatorX(s.lng));
  const ys = sites.map((s) => mercatorY(s.lat));
  const size = sites.map((s) => COIN_SIZE_PX[stageForWeight(s.w) - 1]);
  const tallest = Math.max(0, ...size.map(([, h]) => h));
  // All the interest a coin stands for: its own plus that of the coins it has
  // hidden. Of two equally strong coins, the one for busier surroundings stays.
  const region = sites.map((s) => s.w);
  const minzoom = sites.map(() => 0);

  let shown = sites.map((_, i) => i);
  for (let z = maxZoom; z >= 0 && shown.length > 1; z--) {
    const scale = WORLD_PX_AT_Z0 * 2 ** z;
    const byY = [...shown].sort((a, b) => ys[a] - ys[b]);
    const pxY = byY.map((i) => ys[i] * scale);
    const strongestFirst = [...shown].sort(
      (a, b) =>
        sites[b].w - sites[a].w ||
        region[b] - region[a] ||
        ys[a] - ys[b] ||
        xs[a] - xs[b],
    );
    const hidden = new Set<number>();
    for (const i of strongestFirst) {
      if (hidden.has(i)) continue;
      const x = xs[i] * scale;
      const y = ys[i] * scale;
      const reach = (size[i][1] + tallest) / 2 + COIN_GAP_PX;
      for (
        let k = lowerBound(pxY, y - reach);
        k < byY.length && pxY[k] <= y + reach;
        k++
      ) {
        const j = byY[k];
        if (j === i || hidden.has(j)) continue;
        const touchX =
          Math.abs(xs[j] * scale - x) <
          (size[i][0] + size[j][0]) / 2 + COIN_GAP_PX;
        const touchY =
          Math.abs(pxY[k] - y) < (size[i][1] + size[j][1]) / 2 + COIN_GAP_PX;
        if (touchX && touchY) {
          hidden.add(j);
          minzoom[j] = z + 1;
          region[i] += region[j];
        }
      }
    }
    shown = shown.filter((i) => !hidden.has(i));
  }
  return sites.map((s, i) => ({ ...s, minzoom: minzoom[i] }));
}

/** Stage coins for weighted interest points, shown at whole zooms up to `maxZoom`. */
export function stageCoins(
  points: WeightedPoint[],
  maxZoom: number,
): StageCoin[] {
  return assignMinZooms(coinSites(points), maxZoom);
}
